import { ButtonHTMLAttributes, cloneElement, forwardRef, ReactElement, RefObject } from 'react';
import { cn } from 'utils/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondaryFlat'
  | 'outlineLight'
  | 'outlineDark'
  | 'outlineShadow'
  | 'danger'
  | 'social'
  | 'noOutline'
  | 'link';
export type ButtonSize = 'large' | 'medium' | 'small';

/**
 * Defines base styles which depend on the variant.
 */
const variantToClassName: Record<ButtonVariant, string> = {
  primary: 'bg-gradient-to-b from-indigo-500 to-indigo-600 text-white ring-1 ring-indigo-600 ring-offset-indigo-700',
  secondary: 'bg-white text-slate-700 ring-1 ring-slate-200',
  secondaryFlat: 'bg-white text-indigo-600',
  outlineLight: 'bg-white text-slate-700 ring-1 ring-slate-200',
  outlineDark: 'hover:bg-opacity-15',
  outlineShadow: 'bg-white text-slate-900',
  danger: 'bg-gradient-to-b from-red-500 to-red-600 text-white ring-1 ring-red-600 ring-offset-red-700',
  social: 'box-border bg-white text-slate-700 ring-1 ring-slate-200',
  noOutline: 'text-slate-600 bg-white',
  link: 'text-indigo-500 bg-white'
};

/**
 * Defines base styles which depend on size.
 */
const sizeToClassName: Record<ButtonSize, string> = {
  large: 'h-12 text-base leading-6 rounded-md rounded min-w-[48px]',
  medium: 'h-10 text-[13px] leading-5 rounded-[5px] rounded min-w-[40px]',
  small: 'h-8 text-[13px] leading-4 rounded min-w-[32px]'
};

type PaddingFn = (hasIcon: boolean, hasLabel: boolean) => string;

const getLargePadding: PaddingFn = (hasIcon, hasLabel) => {
  if (hasIcon) {
    if (hasLabel) {
      return 'pr-4 pl-6 py-3';
    }
    return 'p-3';
  }
  return 'px-6 py-3';
};

const getMediumPadding: PaddingFn = (hasIcon, hasLabel) => {
  if (hasIcon) {
    if (hasLabel) {
      return 'pr-3 pl-4 py-2';
    }
    return 'p-2';
  }
  return 'px-4 py-2';
};

const getSmallPadding: PaddingFn = (hasIcon, hasLabel) => {
  if (hasIcon) {
    if (hasLabel) {
      return 'px-3';
    }
    return 'p-2';
  }
  return 'px-3 py-2';
};

/**
 * Defines variations in padding.
 */
const paddings: Record<ButtonSize, PaddingFn> = {
  large: getLargePadding,
  medium: getMediumPadding,
  small: getSmallPadding
};

/**
 * Defines styles which should be applied only if the button is not disabled.
 */
const variantToActiveClassName: Record<ButtonVariant, string> = {
  primary: 'hover:ring-2 hover:ring-indigo-100 hover:ring-offset-1',
  secondary: 'hover:text-slate-900 hover:ring-2 hover:ring-slate-100/50 hover:ring-offset-1 hover:ring-offset-slate-300',
  secondaryFlat: 'hover:bg-indigo-100',
  outlineLight: 'hover:text-slate-900 hover:ring-2 hover:ring-slate-100/50 hover:ring-offset-1 hover:ring-offset-slate-300',
  outlineDark: 'bg-white bg-opacity-5 border border-slate-300 text-white',
  outlineShadow: 'hover:bg-indigo-100 drop-shadow-md',
  danger: 'hover:ring-2 hover:ring-red-100 hover:ring-offset-1',
  social: 'hover:text-slate-900 hover:ring-2 hover:ring-slate-100/50 hover:ring-offset-1 hover:ring-offset-slate-300',
  noOutline: 'hover:text-slate-400',
  link: 'hover:text-indigo-400 hover:text-opacity-25'
};

/**
 * Defines icon styles which depend on the variant.
 */
const variantToIconClassName: Record<ButtonVariant, string> = {
  primary: 'text-indigo-200',
  secondary: 'text-indigo-400',
  secondaryFlat: 'text-indigo-400',
  outlineLight: 'text-slate-400',
  outlineDark: 'text-slate-400',
  outlineShadow: 'text-slate-400',
  danger: 'text-red-300',
  social: 'text-slate-500',
  noOutline: '',
  link: 'text-slate-800'
};

/**
 * Defines icon styles which depend on size.
 */
const sizeToIconSize: Record<ButtonSize, number> = {
  large: 24,
  medium: 24,
  small: 16
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactElement;
  padding?: string;
  iconChildrenOnly?: boolean;
  leftIcon?: boolean;
}

const Button = forwardRef(
  (
    {
      variant = 'primary',
      size = 'large',
      icon,
      iconChildrenOnly,
      leftIcon,
      className,
      disabled,
      children,
      onClick,
      padding,
      ...buttonProps
    }: ButtonProps,
    ref: RefObject<HTMLButtonElement>
  ) => {
    const hasIcon = iconChildrenOnly || icon !== undefined;
    const hasLabel = !iconChildrenOnly && children !== undefined;

    return (
      <button
        ref={ref}
        className={cn(
          'flex cursor-pointer flex-row items-center justify-center rounded font-sans font-semibold outline-0 focus:outline-1',
          { 'flex-row-reverse': leftIcon },
          variantToClassName[variant],
          sizeToClassName[size],
          padding !== undefined ? padding : paddings[size](hasIcon, hasLabel),
          disabled ? 'cursor-auto opacity-50' : variantToActiveClassName[variant],
          className
        )}
        onClick={!disabled ? onClick : () => {}}
        disabled={disabled}
        {...buttonProps}
      >
        {children && (
          <div
            className={cn('flex flex-row items-center justify-center', {
              'mr-2': hasIcon && !iconChildrenOnly && !leftIcon,
              'mr-1.5': hasIcon && !iconChildrenOnly && size === 'small' && !leftIcon,
              'ml-2': hasIcon && !iconChildrenOnly && leftIcon,
              'ml-1.5': hasIcon && !iconChildrenOnly && size === 'small' && leftIcon,
              'drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]': variant === 'primary'
            })}
          >
            {children}
          </div>
        )}
        {icon &&
          cloneElement(icon, {
            className: variantToIconClassName[variant],
            size: sizeToIconSize[size]
          })}
      </button>
    );
  }
);

export default Button;
